import React, { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import getInTouchImg from "../../assets/Images/home/Get-in-Touch-with-Us.png";

// ── EmailJS credentials ───────────────────────────────────────────────
const SERVICE_ID  = "service_gnmq7iu";
const TEMPLATE_ID = "template_lu4mjl9";
const PUBLIC_KEY  = "adDZaNUl_gM8HMMV0";
// ──────────────────────────────────────────────────────────────────────

const INITIAL = { name: "", email: "", phone: "", subject: "", message: "" };

const baseInput =
  "w-full bg-black/20 border rounded-md px-4 py-3 text-white placeholder:text-white/80 text-sm sm:text-base outline-none transition-colors disabled:opacity-60";
const getInputClass = (hasError) =>
  `${baseInput} ${
    hasError
      ? "border-red-300/80 focus:border-red-300 focus:bg-black/30"
      : "border-white/30 focus:border-white/60 focus:bg-black/30"
  }`;

export default function GetInTouch({
  bgImage = getInTouchImg,
  heading = "GET IN TOUCH WITH US",
  description = "From concept to market, Nutralike delivers end-to-end CDMO solutions, including formulation, manufacturing, packaging, and regulatory support.",
  onSubmit,
}) {
  const formRef = useRef(null);
  const successTimerRef = useRef(null);
  const [formData, setFormData] = useState(INITIAL);
  const [verified, setVerified] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle", msg: "" }); // idle | sending | success | error

  useEffect(() => {
    emailjs.init({ publicKey: PUBLIC_KEY });
  }, []);

  useEffect(() => {
    if (status.state === "success") {
      successTimerRef.current = setTimeout(
        () => setStatus({ state: "idle", msg: "" }),
        6000
      );
    }
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, [status.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((errs) => {
        const next = { ...errs };
        delete next[name];
        return next;
      });
    }
    if (status.state === "error") setStatus({ state: "idle", msg: "" });
  };

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Please enter your name.";
    else if (formData.name.trim().length < 2)
      errors.name = "Name must be at least 2 characters.";

    if (!formData.email.trim()) errors.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()))
      errors.email = "Please enter a valid email address.";

    if (!formData.phone.trim()) errors.phone = "Please enter your phone number.";
    else if (!/^[+\d][\d\s()-]{6,}$/.test(formData.phone.trim()))
      errors.phone = "Please enter a valid phone number.";

    if (!formData.subject.trim()) errors.subject = "Please enter a subject.";

    if (!formData.message.trim()) errors.message = "Please enter a message.";
    else if (formData.message.trim().length < 10)
      errors.message = "Message must be at least 10 characters.";

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!verified) {
      setStatus({ state: "error", msg: "Please verify you are human." });
      return;
    }

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setStatus({
        state: "error",
        msg: "Please fix the highlighted fields and try again.",
      });
      return;
    }

    setFieldErrors({});
    setStatus({ state: "sending", msg: "" });

    const templateParams = {
      to_email: "marketing2@nutralike.com,marketing2@nutralike.com,info@nutralike.com",
      name: formData.name.trim(),
      email: formData.email.trim(),
      number: formData.phone.trim(),
      phone: formData.phone.trim(),
      subject: formData.subject.trim(),
      message: formData.message.trim(),
      time: new Date().toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    };

    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, {
        publicKey: PUBLIC_KEY,
      });
      setStatus({
        state: "success",
        msg: "Thank you! Your message has been sent successfully. We'll be in touch shortly.",
      });
      if (onSubmit) onSubmit(formData);
      setFormData(INITIAL);
      setVerified(false);
    } catch (err) {
      const apiMsg =
        typeof err?.text === "string"
          ? err.text
          : typeof err?.message === "string"
          ? err.message
          : "";
      setStatus({
        state: "error",
        msg:
          apiMsg ||
          "Sorry, we couldn't send your message right now. Please try again in a moment or email us at info@nutralike.com.",
      });
    }
  };

  const isSending = status.state === "sending";

  return (
    <section
      className="relative w-full overflow-hidden bg-cover bg-center bg-no-repeat bg-fixed"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      {/* Dark overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(36,54,50,0.75) 0%, rgba(36,54,50,0.55) 50%, rgba(36,54,50,0.75) 100%)",
        }}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* LEFT: heading + description */}
          <div className="text-white">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-wide leading-tight mb-4 sm:mb-6">
              {heading}
            </h2>
            <p className="text-sm sm:text-base text-white/90 leading-relaxed max-w-md">
              {description}
            </p>
          </div>

          {/* RIGHT: form */}
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            noValidate
            className="space-y-4 w-full max-w-xl lg:justify-self-end"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isSending}
                  placeholder="Name"
                  aria-invalid={!!fieldErrors.name}
                  className={getInputClass(!!fieldErrors.name)}
                  aria-label="Name"
                />
                {fieldErrors.name && (
                  <p className="mt-1 text-xs text-red-200">{fieldErrors.name}</p>
                )}
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSending}
                  placeholder="Email"
                  aria-invalid={!!fieldErrors.email}
                  className={getInputClass(!!fieldErrors.email)}
                  aria-label="Email"
                />
                {fieldErrors.email && (
                  <p className="mt-1 text-xs text-red-200">{fieldErrors.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={isSending}
                  placeholder="Phone"
                  aria-invalid={!!fieldErrors.phone}
                  className={getInputClass(!!fieldErrors.phone)}
                  aria-label="Phone"
                />
                {fieldErrors.phone && (
                  <p className="mt-1 text-xs text-red-200">{fieldErrors.phone}</p>
                )}
              </div>

              <div>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={isSending}
                  placeholder="Subject"
                  aria-invalid={!!fieldErrors.subject}
                  className={getInputClass(!!fieldErrors.subject)}
                  aria-label="Subject"
                />
                {fieldErrors.subject && (
                  <p className="mt-1 text-xs text-red-200">{fieldErrors.subject}</p>
                )}
              </div>
            </div>

            <div>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                disabled={isSending}
                placeholder="Message"
                rows={5}
                aria-invalid={!!fieldErrors.message}
                className={`${getInputClass(!!fieldErrors.message)} resize-none min-h-[120px]`}
                aria-label="Message"
              />
              {fieldErrors.message && (
                <p className="mt-1 text-xs text-red-200">{fieldErrors.message}</p>
              )}
            </div>

            {/* Cloudflare-style verification box */}
            <div className="bg-white rounded-md shadow-sm flex items-center justify-between px-4 py-3 w-full max-w-sm">
              <label className="flex items-center gap-3 text-sm text-gray-800 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={verified}
                  onChange={(e) => {
                    setVerified(e.target.checked);
                    if (status.state === "error") setStatus({ state: "idle", msg: "" });
                  }}
                  disabled={isSending}
                  className="w-5 h-5 accent-gray-700 cursor-pointer"
                />
                <span>Verify you are human</span>
              </label>
              <div className="flex flex-col items-end leading-none">
                <div className="flex items-center gap-1.5">
                  <CloudflareLogo className="w-5 h-4" />
                  <span className="text-[10px] font-bold text-gray-800 tracking-widest">
                    CLOUDFLARE
                  </span>
                </div>
                <div className="text-[9px] text-gray-500 mt-1">
                  <a href="#" className="hover:underline">
                    Privacy
                  </a>
                  <span className="mx-1">•</span>
                  <a href="#" className="hover:underline">
                    Help
                  </a>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={!verified || isSending}
              className="mt-2 inline-flex items-center gap-2 px-10 py-3 bg-black/30 border border-white/25 text-white tracking-[0.2em] font-medium text-sm rounded-sm hover:bg-black/50 hover:border-white/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSending && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSending ? "SENDING..." : "SEND"}
            </button>

            {status.state === "success" && (
              <div
                role="status"
                aria-live="polite"
                className="flex items-start gap-2 text-sm text-green-100 bg-green-700/40 border border-green-300/40 px-3 py-2 rounded-md"
              >
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{status.msg}</span>
              </div>
            )}

            {status.state === "error" && (
              <div
                role="alert"
                aria-live="assertive"
                className="flex items-start gap-2 text-sm text-red-100 bg-red-700/40 border border-red-300/40 px-3 py-2 rounded-md"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{status.msg}</span>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function CloudflareLogo({ className = "w-5 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 20"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#F48120"
        d="M22.9 9.7c-.35 0-.7.04-1.03.11C21 6.6 18.14 4.2 14.7 4.2c-3.1 0-5.74 1.96-6.75 4.72-.25-.06-.51-.09-.78-.09-2.2 0-4 1.77-4.03 3.97-.01.44.06.87.2 1.28h20.47c.76-.57 1.26-1.48 1.26-2.5 0-1.75-1.43-3.18-3.18-3.18z"
      />
      <path
        fill="#FAAD3F"
        d="M24.9 13.9c.1-.26.16-.53.17-.82.04-1.77-1.37-3.22-3.14-3.26-.35-.01-.69.04-1.01.14-.03-.02-.05-.04-.08-.05.77-.22 1.58-.34 2.42-.34 4.08 0 7.4 3.32 7.4 7.4 0 .34-.02.67-.07 1h-5.77c0-.03.02-.07.03-.1.05-.18.08-.36.09-.55.03-.86-.47-1.61-1.2-1.94-.4-.18-.85-.3-1.31-.34.5-.26.91-.66 1.18-1.14.01-.02.02-.04.03-.06.13-.21.22-.44.26-.68.02-.13.04-.25.04-.38.01-.14.01-.28 0-.42-.02-.12-.04-.24-.08-.36z"
      />
    </svg>
  );
}
